import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Award, ArrowLeft, Upload, ExternalLink, Trash2, FileText,
  CheckCircle2, XCircle, Clock, Users, Calendar, MapPin, Layers,
  MessageSquare, Send, BookOpen,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { CardSkeleton, ListSkeleton } from "../components/Skeletons";

const STATUS_BADGE = {
  valide: { label: "Validé", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  non_valide: { label: "Non validé", color: "bg-red-500/10 text-red-400 border-red-500/30", icon: XCircle },
  en_attente: { label: "À valider", color: "bg-slate-500/10 text-slate-400 border-slate-500/30", icon: Clock },
};

const PROOF_STATUS = {
  pending: { label: "En attente", color: "text-amber-400" },
  accepted: { label: "Acceptée", color: "text-emerald-400" },
  rejected: { label: "Refusée", color: "text-red-400" },
};

export default function CandidatParcoursPage() {
  const { axiosAuth } = useAuth();
  const { candidate_id } = useParams();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProof, setShowProof] = useState(false);
  const [proofForm, setProofForm] = useState({ bloc_id: "", description: "", external_url: "", file: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/candidate/parcours/${candidate_id}`);
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setLoading(false);
  }, [api, candidate_id]);

  useEffect(() => { load(); }, [load]);

  const submitProof = async () => {
    if (!proofForm.bloc_id) { toast.error("Selectionnez un bloc"); return; }
    if (!proofForm.file && !proofForm.external_url) { toast.error("Fournir un fichier ou une URL"); return; }
    const fd = new FormData();
    fd.append("bloc_id", proofForm.bloc_id);
    fd.append("description", proofForm.description);
    if (proofForm.file) fd.append("file", proofForm.file);
    if (proofForm.external_url) fd.append("external_url", proofForm.external_url);
    try {
      await api.post(`/certifications/${data.certification.certification_id}/candidates/${candidate_id}/proofs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Preuve deposee");
      setShowProof(false);
      setProofForm({ bloc_id: "", description: "", external_url: "", file: null });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteProof = async (proof_id) => {
    if (!window.confirm("Supprimer cette preuve ?")) return;
    try {
      await api.delete(`/certifications/${data.certification.certification_id}/candidates/${candidate_id}/proofs/${proof_id}`);
      toast.success("Supprime");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading || !data) return (
    <div className="p-6 lg:p-10 space-y-6 max-w-5xl mx-auto" data-testid="parcours-loading">
      <CardSkeleton /><CardSkeleton /><CardSkeleton />
    </div>
  );

  const cert = data.certification || {};
  const parcours = data.parcours_blocs || [];
  const proofs = data.proofs || [];
  const valides = parcours.filter(p => p.status === "valide").length;
  const total = parcours.length || 1;
  const progress = Math.round((valides / total) * 100);

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6" data-testid="candidat-parcours-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-400 hover:text-white -ml-2" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour
      </Button>

      {/* HEADER CERTIFICATION */}
      <div className="bg-gradient-to-br from-[#14101c] to-[#1a1625] border border-[#f7c935]/20 rounded-2xl p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2 mb-3">
              <Badge className={cert.type === "RNCP" ? "bg-purple-500/10 text-purple-400 border-purple-500/30" : "bg-blue-500/10 text-blue-400 border-blue-500/30"}>{cert.type}</Badge>
              <span className="text-xs text-[#f7c935] font-mono">{cert.code}</span>
              {cert.niveau && <span className="text-xs text-slate-400">· {cert.niveau}</span>}
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">{cert.title}</h1>
            <p className="text-sm text-slate-400">
              Mode de validation : <span className="text-white">{data.mode_validation}</span> · Statut : <Badge className={data.cert_status === "certified" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30 ml-1" : data.cert_status === "partial" ? "bg-amber-500/10 text-amber-400 border-amber-500/30 ml-1" : "bg-slate-500/10 text-slate-400 border-slate-500/30 ml-1"}>
                {data.cert_status}
              </Badge>
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 mb-1">Progression</div>
            <div className="text-3xl font-bold text-[#f7c935]">{progress}%</div>
            <div className="text-xs text-slate-500">{valides}/{total} blocs valides</div>
          </div>
        </div>
        <div className="mt-4 h-2 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#f7c935] to-emerald-400 transition-all duration-500" style={{ width: `${progress}%` }} data-testid="progress-bar" />
        </div>
      </div>

      {/* JURY */}
      {data.jury && (
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-5" data-testid="jury-card">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-[#f7c935]" />
            <h3 className="text-lg font-semibold text-white">Convocation jury</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div><div className="text-xs text-slate-500 mb-1">Session</div><div className="text-white">{data.jury.session_label}</div></div>
            <div><div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Date</div><div className="text-white">{data.jury.convocation_date}</div></div>
            <div><div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Modalite</div><div className="text-white">{data.jury.modalite}{data.jury.location ? ` · ${data.jury.location}` : ""}</div></div>
          </div>
          {data.jury.members && data.jury.members.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <div className="text-xs text-slate-500 mb-2">{data.jury.members.length} membre{data.jury.members.length > 1 ? "s" : ""}</div>
              <div className="flex flex-wrap gap-2">
                {data.jury.members.map((m, i) => (
                  <Badge key={i} variant="outline" className="text-slate-300 border-white/10 text-xs">
                    {m.full_name} · {m.role}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* BLOCS */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden" data-testid="blocs-section">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#f7c935]" />
            <h3 className="text-lg font-semibold text-white">Blocs de competences</h3>
          </div>
          <Button onClick={() => setShowProof(true)} size="sm" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold" data-testid="add-proof-btn">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Deposer une preuve
          </Button>
        </div>
        <div className="divide-y divide-white/5">
          {parcours.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Aucun bloc dans le parcours</div>
          ) : parcours.map((p, idx) => {
            const status = STATUS_BADGE[p.status] || STATUS_BADGE.en_attente;
            const StatusIcon = status.icon;
            const blocProofs = proofs.filter(pr => pr.bloc_id === p.bloc_id);
            return (
              <div key={p.bloc_id || idx} className="p-5 hover:bg-white/[0.02] transition-colors" data-testid={`bloc-row-${p.bloc_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#f7c935] font-mono">{p.bloc_code || `BLOC-${idx + 1}`}</span>
                      <Badge className={status.color}><StatusIcon className="w-3 h-3 mr-1" />{status.label}</Badge>
                    </div>
                    <h4 className="text-base font-semibold text-white">{p.bloc_title || "Bloc de competences"}</h4>
                    {p.bloc_description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.bloc_description}</p>}
                  </div>
                  {p.score != null && (
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Note</div>
                      <div className="text-xl font-bold text-[#f7c935]">{p.score}/20</div>
                    </div>
                  )}
                </div>
                {p.commentaire && (
                  <div className="bg-[#1a1625] rounded-md p-2 mt-2 text-xs text-slate-400">
                    <span className="font-semibold text-slate-300">Commentaire jury :</span> {p.commentaire}
                  </div>
                )}
                {blocProofs.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {blocProofs.map(pr => (
                      <div key={pr.proof_id} className="flex items-center gap-2 text-xs bg-[#1a1625] rounded p-2">
                        {pr.external_url ? (
                          <ExternalLink className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                        ) : (
                          <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        )}
                        <span className="text-slate-200 flex-1 truncate">{pr.file_name || pr.external_url || "Preuve"}</span>
                        {pr.description && <span className="text-slate-500 hidden md:block">{pr.description}</span>}
                        <span className={`text-xs ${PROOF_STATUS[pr.validation_status]?.color || "text-slate-500"}`}>{PROOF_STATUS[pr.validation_status]?.label || pr.validation_status}</span>
                        <Button size="sm" variant="ghost" onClick={() => deleteProof(pr.proof_id)} className="text-red-400 h-6 w-6 p-0" data-testid={`delete-proof-${pr.proof_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* PARCHEMIN */}
      {(data.cert_status === "certified" || data.cert_status === "partial") && data.parchemin_unique_id && (
        <div className="bg-gradient-to-br from-emerald-500/10 to-[#f7c935]/10 border border-[#f7c935]/30 rounded-xl p-6" data-testid="parchemin-card">
          <div className="flex items-center gap-3 mb-3">
            <Award className="w-8 h-8 text-[#f7c935]" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white">Parchemin disponible</h3>
              <p className="text-xs text-slate-400">Delivre le {(data.parchemin_issued_at || "").slice(0, 10)} · ID {data.parchemin_unique_id}</p>
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/certifications/${cert.certification_id}/candidates/${candidate_id}/parchemin/generate`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-parchemin-btn"
              className="inline-flex items-center gap-2 bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold px-4 py-2 rounded-md text-sm"
            >
              <FileText className="w-4 h-4" /> Telecharger PDF
            </a>
          </div>
          <p className="text-xs text-slate-400">Le parchemin contient un QR code de verification publique. Toute personne peut verifier l'authenticite en scannant le QR ou en visitant <span className="text-[#f7c935]">blitzz.fr/verify/{data.parchemin_unique_id}</span></p>
        </div>
      )}

      {/* MESSAGERIE + REPLAY EVALUATIONS */}
      <Tabs defaultValue="messages" className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden">
        <TabsList className="bg-[#1a1625] border-b border-white/10 p-1 rounded-none w-full justify-start">
          <TabsTrigger value="messages" data-testid="tab-messages" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-black gap-2">
            <MessageSquare className="w-4 h-4" /> Messages
          </TabsTrigger>
          <TabsTrigger value="evaluations" data-testid="tab-evaluations" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-black gap-2">
            <BookOpen className="w-4 h-4" /> Replay evaluations
          </TabsTrigger>
        </TabsList>
        <TabsContent value="messages" className="p-5">
          <CandidateMessagesSection api={api} candidate_id={candidate_id} />
        </TabsContent>
        <TabsContent value="evaluations" className="p-5">
          <EvaluationsReplaySection api={api} candidate_id={candidate_id} />
        </TabsContent>
      </Tabs>

      {/* DIALOG DEPOT PREUVE */}
      <Dialog open={showProof} onOpenChange={setShowProof}>
        <DialogContent className="bg-[#14101c] border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="text-white">Deposer une preuve de competence</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Bloc concerne *</label>
              <Select value={proofForm.bloc_id} onValueChange={v => setProofForm(f => ({ ...f, bloc_id: v }))}>
                <SelectTrigger data-testid="proof-bloc-select" className="bg-[#1a1625] border-white/10"><SelectValue placeholder="Selectionner un bloc" /></SelectTrigger>
                <SelectContent className="bg-[#1a1625] border-white/10">
                  {parcours.map(p => <SelectItem key={p.bloc_id} value={p.bloc_id}>{p.bloc_code} — {p.bloc_title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Description</label>
              <Textarea data-testid="proof-description" value={proofForm.description} onChange={e => setProofForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Decrivez la preuve apportee..." className="bg-[#1a1625] border-white/10" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Fichier (PDF, image, doc)</label>
              <Input data-testid="proof-file" type="file" onChange={e => setProofForm(f => ({ ...f, file: e.target.files[0] || null }))} className="bg-[#1a1625] border-white/10 text-slate-300 file:bg-[#f7c935] file:text-black file:border-0 file:rounded file:px-3 file:py-1 file:mr-3" />
            </div>
            <div className="text-center text-xs text-slate-500">— ou —</div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Lien externe (GitHub, Drive, ...)</label>
              <Input data-testid="proof-url" type="url" value={proofForm.external_url} onChange={e => setProofForm(f => ({ ...f, external_url: e.target.value }))} placeholder="https://..." className="bg-[#1a1625] border-white/10" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowProof(false)}>Annuler</Button>
            <Button onClick={submitProof} data-testid="proof-submit" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold">Deposer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-component : Messagerie ────────────────────────────────

function CandidateMessagesSection({ api, candidate_id }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/candidate/parcours/${candidate_id}/messages`);
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setLoading(false);
  }, [api, candidate_id]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.post(`/candidate/parcours/${candidate_id}/messages`, { body: draft });
      setDraft("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSending(false);
  };

  return (
    <div className="space-y-3" data-testid="messages-section">
      {loading ? <ListSkeleton items={3} /> : items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-6">Aucun message. Demarrez la conversation avec votre formateur.</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {items.map(m => (
            <div key={m.message_id} className="bg-[#1a1625] border border-white/5 rounded-lg p-3" data-testid={`msg-${m.message_id}`}>
              <div className="flex items-center gap-2 mb-1.5 text-xs">
                <span className="font-semibold text-white">{m.from_user_name || "Utilisateur"}</span>
                {m.from_user_role && <Badge variant="outline" className="text-xs border-white/10 text-slate-400 px-1.5 py-0">{m.from_user_role}</Badge>}
                <span className="text-slate-500 ml-auto">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <p className="text-sm text-slate-200 whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Ecrire un message au formateur..."
          rows={2}
          className="bg-[#1a1625] border-white/10 flex-1"
          data-testid="msg-input"
        />
        <Button onClick={send} disabled={sending || !draft.trim()} className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold self-end" data-testid="msg-send">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ─── Sub-component : Replay evaluations ────────────────────────

function EvaluationsReplaySection({ api, candidate_id }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/candidate/parcours/${candidate_id}/evaluations-replay`)
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [api, candidate_id]);

  if (loading) return <ListSkeleton items={2} />;
  if (items.length === 0) return <p className="text-sm text-slate-500 text-center py-6">Aucune evaluation passee.</p>;

  return (
    <div className="space-y-2" data-testid="evaluations-replay">
      {items.map(s => (
        <div key={s.submission_id || s.id} className="bg-[#1a1625] border border-white/5 rounded-lg p-3 flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-[#f7c935] flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-white">{s.evaluation_title || "Evaluation"}</div>
            <div className="text-xs text-slate-500">{(s.submitted_at || "").slice(0, 10)} · {s.evaluation_type || "type non defini"}</div>
          </div>
          {s.score != null && (
            <div className="text-right">
              <div className="text-xs text-slate-500">Score</div>
              <div className="text-base font-bold text-[#f7c935]">{s.score}/20</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
